/** Job id → ISO timestamp of when the user opened the listing (new tab). */
export type VisitedJobsStore = Record<string, string>;

export const VISITED_JOBS_STORAGE_KEY = "calijobs-visited-job-opens-v1";

const VISITED_JOBS_STORAGE_KEY_LEGACY = "pip-visited-job-opens-v1";

/** Visits older than this are dropped from storage and treated as not visited. */
export const VISITED_JOB_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function pruneVisitedJobsStore(
  store: VisitedJobsStore,
  nowMs: number = Date.now(),
): VisitedJobsStore {
  const cutoff = nowMs - VISITED_JOB_MAX_AGE_MS;
  const next: VisitedJobsStore = {};
  for (const [id, iso] of Object.entries(store)) {
    const t = Date.parse(iso);
    if (Number.isFinite(t) && t >= cutoff) next[id] = iso;
  }
  return next;
}

export function parseVisitedJobsFromStorage(raw: string | null): VisitedJobsStore {
  if (!raw) return {};
  try {
    const p = JSON.parse(raw) as unknown;
    if (p === null || typeof p !== "object" || Array.isArray(p)) return {};
    const o: VisitedJobsStore = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof k === "string" && typeof v === "string") o[k] = v;
    }
    return o;
  } catch {
    return {};
  }
}

export function persistVisitedJobsStore(store: VisitedJobsStore): void {
  try {
    localStorage.setItem(VISITED_JOBS_STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Read, prune stale entries, and persist if anything was removed. */
export function readVisitedJobsStore(): VisitedJobsStore {
  if (typeof localStorage === "undefined") return {};
  let raw = localStorage.getItem(VISITED_JOBS_STORAGE_KEY);
  if (raw === null) {
    raw = localStorage.getItem(VISITED_JOBS_STORAGE_KEY_LEGACY);
    if (raw !== null) {
      try {
        localStorage.setItem(VISITED_JOBS_STORAGE_KEY, raw);
        localStorage.removeItem(VISITED_JOBS_STORAGE_KEY_LEGACY);
      } catch {
        /* ignore */
      }
    }
  }
  const parsed = parseVisitedJobsFromStorage(raw);
  const pruned = pruneVisitedJobsStore(parsed);
  if (Object.keys(pruned).length !== Object.keys(parsed).length) {
    persistVisitedJobsStore(pruned);
  }
  return pruned;
}

export function isJobVisitedRecently(
  store: VisitedJobsStore,
  jobId: string,
  nowMs: number = Date.now(),
): boolean {
  const iso = store[jobId];
  if (!iso) return false;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  return t >= nowMs - VISITED_JOB_MAX_AGE_MS;
}
